import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AccountLoading from 'components/library/Loading';
import Button from 'components/library/Button/Button';
import { createNewAccount, fetchAccountDataFromURL } from 'store/features/account/actions';
import { getInvestmentsData } from 'store/features/investments/actions';

import InvestmentsForm from './InvestmentsForm/index';
import InvestmentsSimulation from './InvestmentsSimulation';
import styles from './Investments.module.css';
import * as statuses from 'store/states';
import { AccountState } from 'store/features/account/types';
import { AppDispatch } from 'store/types';
import { TaxRate } from 'store/features/investments/types';

type Simulation = {
  stockPrice: number;
  valuation: number;
  taxRate?: TaxRate;
  minAmount: number;
  minPeaAmount: number;
  amountSought: number;
}

type ErrorForm = {
  phone?: string;
  email?: string;
};

interface Props {
  status: typeof statuses[keyof typeof statuses],
  value: number,
  simulation: Simulation,
  isConnected: boolean,
  isSubmittable: boolean,
  domain: string
  account: AccountState,
  calendar: string,
}

export default function Investments({
  status, value,
  simulation, isConnected, isSubmittable,
  domain, account, calendar,
}: Props) {
  const [errors, setErrors] = useState<ErrorForm>({});
  const [isRiNeeded, setIsRiNeeded] = useState(false);

  const { stockPrice, valuation, taxRate, minAmount, minPeaAmount, amountSought } = simulation;

  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    if (status === 'IDLE') {
      dispatch(getInvestmentsData(parseInt(id || '0')));
    }
    if (status === 'SUCCESS') {
      dispatch(fetchAccountDataFromURL());
    }
  }, [dispatch, id, status]);

  const updateValue = (value: number) => {
    dispatch({ type: 'UPDATE_AMOUNT', payload: value });
    window.sessionStorage.setItem('amount', value.toString());
  };

  const goPayment = () => {
    if (value > 1000000) {
      setIsRiNeeded(true);
    } else {
      let url = `/${id}/paiement`;
      const token = new URL(window.location.href).searchParams.get('token');
      if (token) {
        url = `${url}${url.includes('?') ? '&' : '?'}token=${token}`;
      }
      const email = new URL(window.location.href).searchParams.get('email');
      if (email) {
        url = `${url}${url.includes('?') ? '&' : '?'}email=${email}`;
      }
      window.location.href = url;
    }
  };

  const goCalendar = () => {
    window.open(`${calendar}?name=${account.firstname}&surname=${account.lastname}&email=${account.email}&phone=${account.phone}`, '_blank');
  };

  const goLogin = () => `${(process.env.REACT_APP_DEBUG ? 'https://dev-auth.ayomi.fr' : 'https://auth.ayomi.fr')}/?redirect=${window.location.href}`;

  const setPhoneError = (bool: boolean) => {
    if (bool) {
      setErrors((curr) => ({ ...curr, phone: 'invalid' }));
    } else {
      setErrors((curr) => {
        const { phone, ...otherErrors } = curr;
        return otherErrors;
      })
    }
  }

  const setEmailError = (bool: boolean) => {
    if (bool) {
      setErrors((curr) => ({ ...curr, email: 'invalid' }));
    } else {
      setErrors((curr) => {
        const { email, ...otherErrors } = curr;
        return otherErrors;
      })
    }
  }

  const validate = () => {
    const newErrors = errors;
    if (isSubmittable) return false;
    const regex = new RegExp(/\S+@\S+\.\S+/);
    if (account.email && !regex.test(account.email)) {
      newErrors.email = 'invalid'
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createAccount = () => {
    if (validate()) {
      dispatch(createNewAccount());
    }
  }

  if (status !== 'SUCCESS') {
    return <AccountLoading />
  }

  return (
    <section className={styles.content}>
      {
        (isRiNeeded)
        && (
          <div className={styles.background}>
            <div className={styles.modal}>
              <p className={styles.title}>
                {t('payment.needRi.title')}
              </p>
              <p className={styles.description}>
                {t('payment.needRi.description')}
              </p>
              <Button
                label={t('payment.needRi.calendar')}
                data-cy="ri-calendar-button"
                onButtonClick={goCalendar}
              />
              <Button
                label={t('payment.needRi.close')}
                data-cy="ri-close-button"
                onButtonClick={() => setIsRiNeeded(false)}
                variant="ghost"
              />
            </div>
          </div>
        )
      }
      <div className={styles.formContainer}>
        <InvestmentsForm
          stockPrice={stockPrice}
          minimum={(window.sessionStorage.getItem('pea') === 'true') ? minPeaAmount : minAmount}
          errors={errors}
          setPhoneError={setPhoneError}
          setEmailError={setEmailError}
          isPEA={(window.sessionStorage.getItem('pea') === 'true')}
        >
          <div className={styles.displayMobile}>
            {
              (!isConnected)
                ? (
                  <>
                    <Button
                      label={t('account.noAccount')}
                      className={styles.submit}
                      onButtonClick={createAccount}
                      disabled={(Object.keys(errors).length > 0) || isSubmittable}
                      dataCy="create-account-button-mobile"
                    />
                    <p>
                      {t('account.alreadyAccount')}
                      <a
                        href={goLogin()}
                        className={styles.link}
                        data-cy="login-link-mobile"
                      >
                        {t('account.logIn')}
                      </a>
                    </p>
                  </>
                )
                : (
                  <Button
                    label={t('payment.button.payment')}
                    className={styles.submit}
                    onButtonClick={goPayment}
                    disabled={(value === 0)}
                    dataCy="go-payment-button-mobile"
                  />
                )
            }
          </div>
        </InvestmentsForm>
        <InvestmentsSimulation
          stockPrice={stockPrice}
          maximum={amountSought}
          valuation={valuation}
        >
          <div className={styles.displayDesktop}>
            {
              (!isConnected)
                ? (
                  <>
                    <Button
                      label={t('account.noAccount')}
                      className={styles.submit}
                      onButtonClick={createAccount}
                      disabled={(Object.keys(errors).length > 0) || isSubmittable}
                      dataCy="create-account-button-desktop"
                    />
                    <p>
                      {t('account.alreadyAccount')}
                      <a
                        href={goLogin()}
                        className={styles.link}
                        data-cy="login-link-desktop"
                      >
                        {t('account.logIn')}
                      </a>
                    </p>
                  </>
                )
                : (
                  <Button
                    label={t('payment.button.payment')}
                    className={styles.submit}
                    onButtonClick={goPayment}
                    disabled={(value === 0)}
                    data-cy="go-payment-button-desktop"
                  />
                )
            }
          </div>
        </InvestmentsSimulation>
      </div>
    </section>
  );
}
